/**
 * MonkeyType-inspired theme definitions.
 * ~40 popular themes with 10 color properties each.
 */

export interface MonkeyTypeColors {
  bg: string;
  main: string;
  caret: string;
  sub: string;
  subAlt: string;
  text: string;
  error: string;
  errorExtra: string;
  colorfulError: string;
  colorfulErrorExtra: string;
}

export interface ThemeDefinition {
  id: string;
  name: string;
  isDark: boolean;
  colors: MonkeyTypeColors;
}

export const DEFAULT_THEME_ID = "provenleap";

export const THEMES: ThemeDefinition[] = [
  {
    id: "provenleap",
    name: "ProvenLeap",
    isDark: false,
    colors: {
      bg: "#ffffff",
      main: "#ff6d6f",
      caret: "#ff6d6f",
      sub: "#94a3b8",
      subAlt: "#f1f5f9",
      text: "#1e3a5f",
      error: "#ef4444",
      errorExtra: "#dc2626",
      colorfulError: "#ef4444",
      colorfulErrorExtra: "#dc2626",
    },
  },
  {
    id: "default",
    name: "Plain",
    isDark: false,
    colors: {
      bg: "#f8f9fa",
      main: "#495057",
      caret: "#495057",
      sub: "#adb5bd",
      subAlt: "#e9ecef",
      text: "#212529",
      error: "#dc3545",
      errorExtra: "#b02a37",
      colorfulError: "#dc3545",
      colorfulErrorExtra: "#b02a37",
    },
  },
  {
    id: "paper",
    name: "Paper",
    isDark: false,
    colors: {
      bg: "#eeeeee",
      main: "#444444",
      caret: "#444444",
      sub: "#b2b2b2",
      subAlt: "#dddddd",
      text: "#444444",
      error: "#d70000",
      errorExtra: "#d70000",
      colorfulError: "#d70000",
      colorfulErrorExtra: "#d70000",
    },
  },  {
    id: "midnight",
    name: "Midnight",
    isDark: true,
    colors: {
      bg: "#0b0e13",
      main: "#60759f",
      caret: "#60759f",
      sub: "#394760",
      subAlt: "#141a24",
      text: "#9fadc6",
      error: "#c27070",
      errorExtra: "#c28b70",
      colorfulError: "#c27070",
      colorfulErrorExtra: "#c28b70",
    },
  },
  {
    id: "dark",
    name: "Dark",
    isDark: true,
    colors: {
      bg: "#111111",
      main: "#eeeeee",
      caret: "#eeeeee",
      sub: "#444444",
      subAlt: "#191919",
      text: "#eeeeee",
      error: "#da3333",
      errorExtra: "#791717",
      colorfulError: "#da3333",
      colorfulErrorExtra: "#791717",
    },
  },
  {
    id: "oblivion",
    name: "Oblivion",
    isDark: true,
    colors: {
      bg: "#313231",
      main: "#a5a096",
      caret: "#a5a096",
      sub: "#5d6263",
      subAlt: "#3a3b3b",
      text: "#f7f5f1",
      error: "#dd452e",
      errorExtra: "#9e3423",
      colorfulError: "#dd452e",
      colorfulErrorExtra: "#9e3423",
    },
  },
  {
    id: "serika",
    name: "Serika",
    isDark: false,
    colors: {
      bg: "#e1e1e3",
      main: "#e2b714",
      caret: "#e2b714",
      sub: "#aaaeb3",
      subAlt: "#d1d3d8",
      text: "#323437",
      error: "#da3333",
      errorExtra: "#791717",
      colorfulError: "#da3333",
      colorfulErrorExtra: "#791717",
    },
  },
  {
    id: "serika_dark",
    name: "Serika Dark",
    isDark: true,
    colors: {
      bg: "#323437",
      main: "#e2b714",
      caret: "#e2b714",
      sub: "#646669",
      subAlt: "#2c2e31",
      text: "#d1d0c5",
      error: "#ca4754",
      errorExtra: "#7e2a33",
      colorfulError: "#ca4754",
      colorfulErrorExtra: "#7e2a33",
    },
  },
  {
    id: "retro",
    name: "Retro",
    isDark: false,
    colors: {
      bg: "#dad3c1",
      main: "#1d1b17",
      caret: "#1d1b17",
      sub: "#918b7d",
      subAlt: "#c8c3b3",
      text: "#1d1b17",
      error: "#bf616a",
      errorExtra: "#793e44",
      colorfulError: "#bf616a",
      colorfulErrorExtra: "#793e44",
    },
  },
  {
    id: "dracula",
    name: "Dracula",
    isDark: true,
    colors: {
      bg: "#282a36",
      main: "#bd93f9",
      caret: "#bd93f9",
      sub: "#6272a4",
      subAlt: "#20222c",
      text: "#f8f8f2",
      error: "#ff5555",
      errorExtra: "#f1fa8c",
      colorfulError: "#ff5555",
      colorfulErrorExtra: "#f1fa8c",
    },
  },
  {
    id: "monokai",
    name: "Monokai",
    isDark: true,
    colors: {
      bg: "#272822",
      main: "#a6e22e",
      caret: "#66d9ef",
      sub: "#e6db74",
      subAlt: "#1f201b",
      text: "#e2e2dc",
      error: "#f92672",
      errorExtra: "#fd971f",
      colorfulError: "#f92672",
      colorfulErrorExtra: "#fd971f",
    },
  },
  {
    id: "nord",
    name: "Nord",
    isDark: true,
    colors: {
      bg: "#242933",
      main: "#88c0d0",
      caret: "#eceff4",
      sub: "#929aaa",
      subAlt: "#2e3440",
      text: "#d8dee9",
      error: "#bf616a",
      errorExtra: "#793e44",
      colorfulError: "#bf616a",
      colorfulErrorExtra: "#793e44",
    },
  },
  {
    id: "catppuccin",
    name: "Catppuccin",
    isDark: true,
    colors: {
      bg: "#1e1e2e",
      main: "#cba6f7",
      caret: "#f2cdcd",
      sub: "#7f849c",
      subAlt: "#181825",
      text: "#cdd6f4",
      error: "#f38ba8",
      errorExtra: "#eba0ac",
      colorfulError: "#f38ba8",
      colorfulErrorExtra: "#eba0ac",
    },
  },
  {
    id: "solarized_light",
    name: "Solarized Light",
    isDark: false,
    colors: {
      bg: "#fdf6e3",
      main: "#859900",
      caret: "#dc322f",
      sub: "#2aa198",
      subAlt: "#e2d8be",
      text: "#181819",
      error: "#d33682",
      errorExtra: "#9b225c",
      colorfulError: "#d33682",
      colorfulErrorExtra: "#9b225c",
    },
  },
  {
    id: "rose_pine",
    name: "Rose Pine",
    isDark: true,
    colors: {
      bg: "#1f1d27",
      main: "#9ccfd8",
      caret: "#f6c177",
      sub: "#c4a7e7",
      subAlt: "#282533",
      text: "#e0def4",
      error: "#eb6f92",
      errorExtra: "#ebbcba",
      colorfulError: "#eb6f92",
      colorfulErrorExtra: "#ebbcba",
    },
  },
  {
    id: "rose_pine_dawn",
    name: "Rose Pine Dawn",
    isDark: false,
    colors: {
      bg: "#fffaf3",
      main: "#56949f",
      caret: "#ea9d34",
      sub: "#c4a7e7",
      subAlt: "#f0e9df",
      text: "#286983",
      error: "#b4637a",
      errorExtra: "#d7827e",
      colorfulError: "#b4637a",
      colorfulErrorExtra: "#d7827e",
    },
  },
  {
    id: "rose_pine_moon",
    name: "Rose Pine Moon",
    isDark: true,
    colors: {
      bg: "#2a273f",
      main: "#9ccfd8",
      caret: "#f6c177",
      sub: "#c4a7e7",
      subAlt: "#211f32",
      text: "#e0def4",
      error: "#eb6f92",
      errorExtra: "#ebbcba",
      colorfulError: "#eb6f92",
      colorfulErrorExtra: "#ebbcba",
    },
  },
  {
    id: "material",
    name: "Material",
    isDark: true,
    colors: {
      bg: "#263238",
      main: "#80cbc4",
      caret: "#80cbc4",
      sub: "#4c6772",
      subAlt: "#2e3c43",
      text: "#e6edf3",
      error: "#fb4934",
      errorExtra: "#cc241d",
      colorfulError: "#fb4934",
      colorfulErrorExtra: "#cc241d",
    },
  },
  {
    id: "vscode",
    name: "VS Code",
    isDark: true,
    colors: {
      bg: "#1e1e1e",
      main: "#007acc",
      caret: "#569cd6",
      sub: "#4d4d4d",
      subAlt: "#191919",
      text: "#d4d4d4",
      error: "#f44747",
      errorExtra: "#f44747",
      colorfulError: "#f44747",
      colorfulErrorExtra: "#f44747",
    },
  },
  {
    id: "github",
    name: "GitHub",
    isDark: true,
    colors: {
      bg: "#212830",
      main: "#41ce5c",
      caret: "#41ce5c",
      sub: "#788386",
      subAlt: "#141b23",
      text: "#ccdae6",
      error: "#c23e3a",
      errorExtra: "#c23e3a",
      colorfulError: "#c23e3a",
      colorfulErrorExtra: "#c23e3a",
    },
  },
  {
    id: "8008",
    name: "8008",
    isDark: true,
    colors: {
      bg: "#333a45",
      main: "#f44c7f",
      caret: "#f44c7f",
      sub: "#939eae",
      subAlt: "#2e343d",
      text: "#e9ecf0",
      error: "#da3333",
      errorExtra: "#791717",
      colorfulError: "#c5da33",
      colorfulErrorExtra: "#849224",
    },
  },
  {
    id: "9009",
    name: "9009",
    isDark: false,
    colors: {
      bg: "#eeebe2",
      main: "#080909",
      caret: "#7fa480",
      sub: "#99947f",
      subAlt: "#d3cfc1",
      text: "#080909",
      error: "#c87e74",
      errorExtra: "#a56961",
      colorfulError: "#c87e74",
      colorfulErrorExtra: "#a56961",
    },
  },
  {
    id: "godspeed",
    name: "Godspeed",
    isDark: false,
    colors: {
      bg: "#eae4cf",
      main: "#9abbcd",
      caret: "#f4d476",
      sub: "#ada998",
      subAlt: "#ded9c9",
      text: "#646669",
      error: "#ca4754",
      errorExtra: "#7e2a33",
      colorfulError: "#ca4754",
      colorfulErrorExtra: "#7e2a33",
    },
  },
  {
    id: "phantom",
    name: "Phantom",
    isDark: true,
    colors: {
      bg: "#000011",
      main: "#7aa2f7",
      caret: "#bb9af7",
      sub: "#414868",
      subAlt: "#24283b",
      text: "#c0caf5",
      error: "#f7768e",
      errorExtra: "#db4b4b",
      colorfulError: "#ff7a93",
      colorfulErrorExtra: "#ff9e64",
    },
  },
  {
    id: "bento",
    name: "Bento",
    isDark: true,
    colors: {
      bg: "#2d394d",
      main: "#ff7a90",
      caret: "#ff7a90",
      sub: "#4a768d",
      subAlt: "#263041",
      text: "#fffaf8",
      error: "#ee2a3a",
      errorExtra: "#f04040",
      colorfulError: "#fc2032",
      colorfulErrorExtra: "#f04040",
    },
  },
  {
    id: "aurora",
    name: "Aurora",
    isDark: true,
    colors: {
      bg: "#011926",
      main: "#00e980",
      caret: "#00e980",
      sub: "#245c69",
      subAlt: "#000c13",
      text: "#ffffff",
      error: "#b94da1",
      errorExtra: "#9b3a76",
      colorfulError: "#b94da1",
      colorfulErrorExtra: "#9b3a76",
    },
  },
  {
    id: "terminal",
    name: "Terminal",
    isDark: true,
    colors: {
      bg: "#191a1b",
      main: "#79a617",
      caret: "#79a617",
      sub: "#48494b",
      subAlt: "#141516",
      text: "#e7eae0",
      error: "#a61717",
      errorExtra: "#731010",
      colorfulError: "#a61717",
      colorfulErrorExtra: "#731010",
    },
  },
  {
    id: "dots",
    name: "Dots",
    isDark: true,
    colors: {
      bg: "#121520",
      main: "#ffffff",
      caret: "#ffffff",
      sub: "#676e8a",
      subAlt: "#1b1e2c",
      text: "#ffffff",
      error: "#da3333",
      errorExtra: "#791717",
      colorfulError: "#da3333",
      colorfulErrorExtra: "#791717",
    },
  },
  {
    id: "sonokai",
    name: "Sonokai",
    isDark: true,
    colors: {
      bg: "#2c2e34",
      main: "#9ed072",
      caret: "#f38c71",
      sub: "#e7c664",
      subAlt: "#232429",
      text: "#e2e2e3",
      error: "#fc5d7c",
      errorExtra: "#ecac6a",
      colorfulError: "#fc5d7c",
      colorfulErrorExtra: "#ecac6a",
    },
  },
  {
    id: "discord",
    name: "Discord",
    isDark: true,
    colors: {
      bg: "#313338",
      main: "#5a65ea",
      caret: "#5a65ea",
      sub: "#565861",
      subAlt: "#2b2d31",
      text: "#dcdee3",
      error: "#df4f4b",
      errorExtra: "#df4f4b",
      colorfulError: "#df4f4b",
      colorfulErrorExtra: "#df4f4b",
    },
  },
];
